import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import moment from "moment";
import { BsDropdownDirective } from "ngx-bootstrap/dropdown";

const TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

@Component({
  selector: "time-picker-input",
  templateUrl: "./time-picker-input.component.html",
  styleUrls: ["./time-picker-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TimePickerInputComponent),
    },
  ],
})
export class TimePickerInputComponent implements OnInit, ControlValueAccessor {
  @Input() start = "09:00";
  @Input() end = "18:00";
  @Input() interval = 15;
  @Input() keyboardInterval = 5;
  @Input() label = "time";
  @Output() timeChanged = new EventEmitter<string>();

  @ViewChild("input", { static: true })
  input: ElementRef;
  @ViewChild(BsDropdownDirective, { static: false })
  dropdown: BsDropdownDirective;

  propagateChange: (value: any) => void;
  propagateTouch: () => void;

  times: string[] = [];

  constructor(private renderer: Renderer2) {}

  ngOnInit() {
    const start = moment(this.start, "HH:mm");
    const end = moment(this.end, "HH:mm");
    this.times = this.generateTimeStops(start, end);
  }

  writeValue(value: any): void {
    this.renderer.setProperty(this.input.nativeElement, "value", value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.renderer.setProperty(this.input.nativeElement, "disabled", disabled);
  }

  handleTimeSelected(time: string): void {
    this.updateValue(time);
  }

  handleInput(value: string): void {
    this.propagateChange(value);
    if (this.dropdown?.isOpen) {
      this.dropdown.hide();
    }
  }

  handleBlur(): void {
    const value = this.input.nativeElement.value;
    const normalized = this.normalizeTime(value.trim());
    if (normalized !== value && TIME_PATTERN.test(normalized)) {
      this.updateValue(normalized);
    }
    this.propagateTouch();
  }

  select(): void {
    this.input.nativeElement.select();
  }

  @HostListener("keyup", ["$event"])
  handleArrowUpDownEvent(event: KeyboardEvent) {
    const value = this.input.nativeElement.value;
    const valid = TIME_PATTERN.test(value);

    if (value.length > 5) {
      this.updateValue(value.substring(0, 5));
    } else if (["ArrowUp", "ArrowDown"].includes(event.key) && valid) {
      const time =
        event.key === "ArrowUp"
          ? moment(value, "HH:mm").add(this.keyboardInterval, "m")
          : moment(value, "HH:mm").subtract(this.keyboardInterval, "m");
      this.updateValue(time.format("HH:mm"));
    } else if (/^\d{4}$/.test(value)) {
      this.updateValue(value.slice(0, 2) + ":" + value.slice(2));
    }
  }

  private updateValue(value: string): void {
    this.writeValue(value);
    this.propagateChange(value);
    this.timeChanged.emit(value);
  }

  private normalizeTime(value: string): string {
    if (/^\d{3,4}$/.test(value)) {
      const padded = value.padStart(4, "0");
      return padded.slice(0, 2) + ":" + padded.slice(2);
    }

    if (/^\d:\d{2}$/.test(value)) {
      return `0${value}`;
    }

    return value;
  }

  private generateTimeStops(
    startTime: moment.Moment,
    endTime: moment.Moment
  ): string[] {
    const timeStops = [];
    while (startTime.isSameOrBefore(endTime)) {
      timeStops.push(moment(startTime));
      startTime.add(this.interval, "m");
    }
    return timeStops.map((time) => time.format("HH:mm"));
  }
}
